import { HttpParams } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { ActivatedRoute, Params, Router } from '@angular/router';
import { FilterDescriptor, FilterOperator } from '@progress/kendo-data-query';
import { isDate } from 'moment';
import * as rison from 'rison-node';
import { DEFAULT_STATE, DashboardState } from './dashboard-state-service';

export interface CyborgRouterSearchLink {
  routerLink: string | string[];
  queryParams: Params;
}

@Injectable({ providedIn: 'root' })
export class DashboardUrlService {
  constructor(private router: Router, private activatedRoute: ActivatedRoute) {}

  /**
   * Navigate to the search results for the given dashboard state.
   * @param hunt search state
   */
  public gotoUrl(state: Partial<DashboardState>): void {
    const searchParams = this.stateToUrlParams(state);

    this.router.navigate([], {
      relativeTo: this.activatedRoute,
      queryParams: searchParams,
      queryParamsHandling: 'merge', // remove to replace all query params by provided
    });
  }

  /**
   * Transform a rison query parameter into a search state.
   * @param query active router query params
   * @returns search state
   */
  public searchStateFromString(query: Params): DashboardState {
    return {
      ...DEFAULT_STATE,
      ...rison.decode(query['state']),
    };
  }

  private stateToUrlParams(state: Partial<DashboardState>): Params {
    const targetState = { ...DEFAULT_STATE, ...state };

    const defaultKeys: string[] = Object.keys(DEFAULT_STATE);
    Object.keys(targetState).forEach((key) => {
      if (!defaultKeys.some((dkey) => dkey === key)) {
        delete targetState[key];
      }
    });

    return {
      state: rison.encode(targetState),
    };
  }

  public stateToHttpParams(filters: FilterDescriptor[]): HttpParams {
    let params = new HttpParams();

    if (filters)
      filters.forEach((value) => {
        const filteredValue = value as FilterDescriptor;
        if (
          filteredValue.value &&
          Array.isArray(filteredValue.value) &&
          filteredValue.value.length
        ) {
          filteredValue.value.forEach((id) => {
            params = params.append(filteredValue.field as string, id);
          });
        } else {
          if (
            (filteredValue.operator as string) ==
            FilterOperator.GreaterThanOrEqual
          )
            params = params.append(
              (filteredValue.field as string) + 'Start',
              filteredValue.value,
            );
          else if (
            (filteredValue.operator as string) == FilterOperator.LessThanOrEqual
          ) {
            if (isDate(filteredValue.value)) {
              const adjustedDate = new Date(filteredValue.value);
              adjustedDate.setDate(adjustedDate.getDate() + 1);
              params = params.append(
                (filteredValue.field as string) + 'End',
                adjustedDate.toString(),
              );
            } else
              params = params.append(
                (filteredValue.field as string) + 'End',
                filteredValue.value,
              );
          } else
            params = params.append(
              filteredValue.field as string,
              filteredValue.value,
            );
        }
      });
    return params;
  }
}
